import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, EllipsisVertical, Pin, Search, X } from "lucide-react";
import { useAuthenticationContext } from "@/store/authContext/authContext";
import Flash from "@/components/deck/Flash";
import { deleteDeck, getDecks } from "@/lib/serverComm";
import { Deck } from "@/common/decks";
import { formatDate } from "@/common/dateUtils";
import { useDeckActions } from "@/hooks/useDeckActions";
import EventBus from "@/lib/eventBus";
import OptionsDropdown from "@/components/deck/OptionsDropdown";
import DeckCard from "@/components/deck/DeckCard";

// Types for better type safety
interface DeckError {
  message: string;
  type: "fetch" | "delete" | "update";
}

// Constants
const SEARCH_DEBOUNCE_MS = 300;
const MAX_SEARCH_RESULTS = 4;

const Decks: React.FC = () => {
  // State management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<string | null>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [decks, setDecks] = useState<Deck[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeckId, setSelectedDeckId] = useState<string | null>(null);
  const [error, setError] = useState<DeckError | null>(null);

  // Hooks
  const { dispatch } = useAuthenticationContext();

  // Memoized filtered decks for performance
  const { pinnedDecks, unpinnedDecks, filteredSearchResults } = useMemo(() => {
    const pinned = decks.filter((deck) => deck.pinned);
    const unpinned = decks.filter((deck) => !deck.pinned);
    const searchResults = searchQuery
      ? decks
          .filter((deck) =>
            deck.name.toLowerCase().includes(searchQuery.toLowerCase()),
          )
          .slice(0, MAX_SEARCH_RESULTS)
      : [];

    return {
      pinnedDecks: pinned,
      unpinnedDecks: unpinned,
      filteredSearchResults: searchResults,
    };
  }, [decks, searchQuery]);

  // Optimized fetch function with error handling
  const fetchDecks = useCallback(async (query: string = "") => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDecks(query, false);
      setDecks(response || []);
      console.log("Fetched decks:", response);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch decks";
      setError({ message: errorMessage, type: "fetch" });
      console.error("Error fetching decks:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    EventBus.$on("decks:reload", fetchDecks);

    // Cleanup subscription on unmount
    return () => {
      EventBus.$off("decks:reload", fetchDecks);
    };
  }, [fetchDecks]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchDecks(searchQuery);
      },
      searchQuery ? SEARCH_DEBOUNCE_MS : 0,
    );

    return () => clearTimeout(timeoutId);
  }, [searchQuery, fetchDecks]);

  // Memoized event handlers to prevent unnecessary re-renders
  const handleDeleteDeck = useCallback(async () => {
    if (!selectedDeckId) return;

    try {
      setError(null);
      await deleteDeck(selectedDeckId);
      EventBus.$emit("flash:open", "success", "Deck deleted successfully");
      setDecks((prevDecks) =>
        prevDecks.filter((deck) => deck.id !== selectedDeckId),
      );
      setIsModalOpen(false);
      setSelectedDeckId(null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete deck";
      setError({ message: errorMessage, type: "delete" });
      console.error("Error deleting deck:", err);
    }
  }, [selectedDeckId]);

  const openDeleteModal = useCallback((deckId: string) => {
    setSelectedDeckId(deckId);
    setIsModalOpen(true);
  }, []);

  // Custom hook for deck actions
  const deckActions = useDeckActions({
    onDeckUpdate: (updatedDeck) => {
      setDecks((prevDecks) =>
        prevDecks.map((d) => (d.id === updatedDeck.id ? updatedDeck : d)),
      );
    },
    onError: setError,
    onDeleteRequest: openDeleteModal,
    onDropdownToggle: (deckId) => {
      setIsDropdownOpen((prev) => (prev === deckId ? null : deckId || null));
    },
  });

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedDeckId(null);
  }, []);

  const handleNavigateBack = useCallback(() => {
    // Navigate to the root/home page of the app
    window.location.href = "/";
  }, []);

  // Optimized click outside effect with proper cleanup
  useEffect(() => {
    const handleClickOutside = () => {
      setIsDropdownOpen(null);
    };

    if (isDropdownOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    // Always return cleanup function to prevent memory leaks
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    // Main Container
    <div className="flex bg-deck-bg font-dm_sans antialiased text-base">
      {/* Modal */}
      <div
        className={`${
          isModalOpen ? "block" : "hidden"
        } absolute flex justify-center items-center z-50 w-screen h-screen bg-black/50`}
      >
        <div className="relative bg-white rounded-xl shadow-lg w-1/4">
          <X
            className="absolute right-2 top-2 text-deck-primary cursor-pointer"
            size={24}
            onClick={handleModalClose}
          />
          <div className="block p-8">
            <span className="block text-deck-primary text-4xl font-bold mb-6">
              Are you sure you want to delete this deck?
            </span>
            <span className="block text-deck-primary text-lg">
              This action is permanent and can’t be undone.
            </span>
          </div>
          <div className="flex justify-between px-8 py-4 border-t border-deck-500">
            <button
              className="transition px-4 py-2 bg-white border border-deck-500 text-deck-primary rounded-full font-semibold hover:bg-deck-primary hover:text-white"
              onClick={handleModalClose}
            >
              Cancel
            </button>
            <button
              className="transition px-4 py-2 bg-deck-danger text-white rounded-full font-semibold hover:bg-white hover:text-deck-danger"
              onClick={handleDeleteDeck}
            >
              Delete Deck
            </button>
          </div>
        </div>
      </div>
      {/* Modal */}

      {/* Flash Notifications */}
      <Flash />
      {/* Flash Notifications */}

      {/* Error Display */}
      {error && (
        <div className="fixed top-4 right-4 z-50 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-md">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline">{error.message}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setError(null)}
          >
            <X size={16} />
          </button>
        </div>
      )}
      {/* Error Display */}

      {/* Left Sidebar */}
      <div className="w-20 bg-white shadow-md"></div>

      {/* Main Content Area */}
      <div className="flex-1 p-8 min-h-screen">
        {/* Header */}
        <header className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <ArrowLeft
              className="text-deck-primary cursor-pointer hover:text-deck-primary/80 transition-colors"
              size={24}
              onClick={handleNavigateBack}
            />
            <h1 className="text-4xl font-semibold text-deck-primary">
              Deck Builder
              <span className="text-sm text-primary font-bold align-super ml-2">
                BETA
              </span>
            </h1>
          </div>

          {/* Search */}
          <div className="flex items-center gap-x-6">
            <div className="group relative transition w-[468px] px-3 py-2 bg-white border border-deck-borders rounded-lg focus-within:border-deck-primary">
              <Search className="absolute text-deck-800" size={24} />
              <X
                className={`${
                  isSearchFocused && searchQuery ? "block" : "hidden"
                } absolute right-2 text-deck-primary cursor-pointer`}
                size={24}
                onClick={handleClearSearch}
              />
              <input
                type="text"
                placeholder="Search for a deck"
                className="w-full px-8 py-0 border-0 focus:outline-0 focus:ring-0"
                value={searchQuery}
                onChange={(e) => handleSearchQueryChange(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
              />

              {/* Search Results */}
              <div
                className={`${
                  isSearchFocused ? "block" : "hidden"
                } rounded-md shadow-md absolute z-10 top-full left-0 w-full bg-white translate-y-2 text-deck-primary`}
              >
                {/* header */}
                <div className="flex align-center justify-between gap-x-4 px-4 py-2 border-b border-deck-500">
                  <span className="flex-1 font-semibold">Name</span>
                  <span className="flex-1 font-semibold text-right">
                    Last modified
                  </span>
                </div>

                {/* results rows */}
                {searchQuery &&
                  filteredSearchResults.map((deck) => (
                    <div
                      key={deck.id}
                      className="flex align-center justify-between gap-x-4 px-4 py-2 border-b border-deck-500 cursor-pointer hover:bg-deck-800/5"
                      onClick={() => deckActions.onNavigate(deck.id)}
                    >
                      <span className="flex-1 font-semibold">{deck.name}</span>
                      <div className="flex justify-end items-center gap-x-2">
                        <span>{formatDate(deck.updated_at)}</span>
                        {deck.pinned && (
                          <Pin
                            className="text-deck-primary cursor-pointer fill-deck-primary"
                            size={20}
                          />
                        )}
                        {!deck.pinned && <i className="w-5 h-5 block" />}
                      </div>
                    </div>
                  ))}

                {/* footer - show see all results if there are more than 4 matches */}
                {searchQuery &&
                  decks.filter((deck) =>
                    deck.name.toLowerCase().includes(searchQuery.toLowerCase()),
                  ).length > MAX_SEARCH_RESULTS && (
                    <div className="flex align-center justify-start gap-x-2 px-4 py-4">
                      <Search className="text-deck-primary" size={24} />
                      <span className="font-semibold">
                        See all search results for "{searchQuery}"
                      </span>
                    </div>
                  )}

                {/* Show message if no results and user is searching */}
                {searchQuery && filteredSearchResults.length === 0 && (
                  <div className="p-8 text-center">
                    <p>
                      No items match your search. Try adjusting your search and
                      try again.
                    </p>
                  </div>
                )}

                {/* footer */}
                <div className="flex align-center justify-start gap-x-2 px-4 py-4">
                  <Search className="text-deck-primary" size={24} />
                  <span className="font-semibold">
                    See all search results for “Attr”
                  </span>
                </div>

                {/* No results */}
                <div className="hidden p-8 text-center">
                  <p>
                    No items match your search. Try adjusting your search and
                    try again.
                  </p>
                </div>
              </div>
            </div>
            <button
              className="transition px-4 py-2 bg-deck-primary text-white rounded-full font-semibold hover:bg-deck-primary/90"
              onClick={() => dispatch({ type: "openNewDeckModal" })}
            >
              Create Deck
            </button>
          </div>
        </header>

        {/* Pinned */}
        <div className="block mb-10">
          <div className="block text-xl font-bold text-deck-primary mb-8">
            Pinned
          </div>
          {loading ? (
            <div className="text-deck-primary">Loading decks...</div>
          ) : pinnedDecks.length > 0 ? (
            <div className="flex gap-4 flex-wrap">
              {pinnedDecks.map((deck) => (
                <DeckCard
                  key={deck.id}
                  deck={deck}
                  isDropdownOpen={isDropdownOpen === deck.id}
                  actions={deckActions}
                />
              ))}
            </div>
          ) : (
            <div className="text-deck-primary">No pinned decks found.</div>
          )}
        </div>
        {/* Pinned */}

        {/* All Decks */}
        <div className="block">
          <div className="block text-xl font-bold text-deck-primary mb-4">
            All Decks
          </div>

          {/* header */}
          <div className="flex w-full mb-4 text-deck-primary">
            <div className="font-bold w-[30%]">Name</div>
            <div className="font-bold w-[15%] pl-1">Last modified</div>
            <div className="font-bold w-[15%]">Date created</div>
            <div className="font-bold w-[40%]">Topic</div>
          </div>

          {/* row list */}
          {loading ? (
            <div className="text-deck-primary">Loading decks...</div>
          ) : unpinnedDecks.length > 0 ? (
            unpinnedDecks.map((deck) => (
              <div
                key={deck.id}
                className="flex w-full mb-4 text-deck-primary bg-white rounded-md py-2.5 px-2"
              >
                <div className="font-bold w-[30%] flex gap-x-2">
                  <span
                    className="cursor-pointer hover:text-deck-primary/80 transition-colors"
                    onClick={() => deckActions.onNavigate(deck.id)}
                  >
                    {deck.name}
                  </span>
                </div>
                <div className="w-[15%]">{formatDate(deck.updated_at)}</div>
                <div className="w-[15%]">{formatDate(deck.created_at)}</div>
                <div className="w-[40%] flex justify-between">
                  <span>
                    {deck.topic
                      ? deck.topic.charAt(0).toUpperCase() + deck.topic.slice(1)
                      : "General"}
                  </span>
                  <div className="relative shrink-0 flex items-start align-center gap-x-2">
                    <Pin
                      className="text-deck-primary cursor-pointer"
                      size={20}
                      onClick={() => deckActions.onTogglePin(deck)}
                    />
                    <div className="relative">
                      <EllipsisVertical
                        className="text-deck-primary cursor-pointer"
                        size={20}
                        onClick={(e) => {
                          e.stopPropagation();
                          deckActions.onToggleDropdown(deck.id);
                        }}
                      />

                      {/* Options dropdown */}
                      <OptionsDropdown
                        position="left"
                        isOpen={isDropdownOpen === deck.id}
                        deck={deck}
                        onTogglePin={deckActions.onTogglePin}
                        onDelete={deckActions.onDelete}
                        onEdit={deckActions.onNavigate}
                        onClose={() => deckActions.onToggleDropdown(deck.id)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-deck-primary">No unpinned decks found.</div>
          )}
        </div>
        {/* All Decks */}
      </div>
    </div>
  );
};
export default Decks;
